/**
 * Application Technical Configuration
 *
 * In deployed environments, config is loaded from /config.json (generated by CDK at
 * deploy time, overlaying these defaults). In local development, config falls back to
 * the default values below.
 *
 * Use resources.ts for UI strings and labels.
 */

export interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
    useMock: boolean;
  };
  auth: {
    authority: string;
    clientId: string;
    scopes: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    /**
     * Cognito hosted-UI domain prefix (the `xxx` in `https://xxx.auth.<region>.amazoncognito.com`).
     * Cognito has no OIDC-standard `end_session_endpoint`, so full IdP sign-out requires this
     * separately-configured value -- it is not derivable from `authority`. Empty by default;
     * supply it via /config.json once known. See getCognitoLogoutUrl().
     */
    cognitoDomain: string;
  };
  environment: {
    name: string;
    version: string;
    isDebug: boolean;
  };
}

/** Runtime overlay from /config.json (CDK-deployed). */
export interface RuntimeConfigOverlay {
  api?: Partial<AppConfig['api']>;
  auth?: Partial<AppConfig['auth']>;
  environment?: Partial<AppConfig['environment']>;
}

const defaultConfig: AppConfig = {
  api: {
    baseUrl: 'http://localhost:3000',
    timeout: 10000,
    useMock: true,
  },
  auth: {
    // Shared "dev-really-admin" Cognito user pool used by axetay-harbour-ui-app, nazem-dashboard,
    // and the really-data Explorer -- verified live via `aws cognito-idp list-user-pools`
    // (really-dev account) 2026-08-27; its OIDC discovery endpoint returns 200. The previous
    // value here (eu-west-2_dj4iLv0cs / svea1d3tsunrrjhf7p2dpj14g) was stale -- traced to a
    // leftover comment in the `really` monorepo's platform app.ts referencing an old,
    // since-recreated pool. That repo computes the live value dynamically
    // (`platformStack.identity.userPool.userPoolId`) and has no SSM parameter publishing it for
    // cross-repo consumption, so there's no durable source to read this from yet -- if this pool
    // is ever recreated again, this value goes stale the same way. Worth asking the `really` repo
    // to publish it to SSM (mirroring this repo's own `UiApiKeyIdParameter` pattern in
    // enrich-api-stack.ts) rather than re-copying it by hand next time.
    authority: 'https://cognito-idp.eu-west-2.amazonaws.com/eu-west-2_87cm3OrdQ',
    clientId: '27l9ngl2amukigh1fl36sfb0ru',
    scopes: 'openid profile email',
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    cognitoDomain: '',
  },
  environment: {
    name: 'development',
    version: '0.1.0',
    isDebug: true,
  },
};

let _config: AppConfig = { ...defaultConfig };

/**
 * Loads runtime configuration from /config.json.
 * Falls back to default config if the file doesn't exist (local dev).
 * Must be called before the app renders.
 */
export async function loadConfig(): Promise<AppConfig> {
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const runtimeConfig: RuntimeConfigOverlay = await response.json();
      _config = {
        api: { ...defaultConfig.api, ...runtimeConfig.api },
        auth: { ...defaultConfig.auth, ...runtimeConfig.auth },
        environment: { ...defaultConfig.environment, ...runtimeConfig.environment },
      };
    }
  } catch {
    // In development, config.json won't exist -- use defaults
    console.log('No runtime config found, using defaults');
  }
  return _config;
}

export const config: AppConfig = new Proxy(defaultConfig, {
  get(_target, prop: string) {
    return _config[prop as keyof AppConfig];
  },
}) as AppConfig;

/** True when runtime has provided an OIDC authority and client (e.g. from /config.json). */
export function isAuthConfigured(): boolean {
  return Boolean(_config.auth.authority && _config.auth.clientId);
}

/**
 * Builds the Cognito hosted-UI `/logout` URL that fully signs the user out of the shared
 * IdP session (not just this app's local oidc-client-ts session). Returns undefined when
 * `auth.cognitoDomain` hasn't been supplied (the default -- see AppConfig.auth.cognitoDomain)
 * or when the region can't be parsed out of `authority`, so callers can fall back to a
 * local-only sign-out instead of building a broken URL.
 */
export function getCognitoLogoutUrl(): string | undefined {
  const { cognitoDomain, authority, clientId, postLogoutRedirectUri } = _config.auth;
  if (!cognitoDomain) return undefined;

  const region = /^https:\/\/cognito-idp\.([a-z0-9-]+)\.amazonaws\.com\//.exec(authority)?.[1];
  if (!region) return undefined;

  const params = new URLSearchParams({ client_id: clientId, logout_uri: postLogoutRedirectUri });
  return `https://${cognitoDomain}.auth.${region}.amazoncognito.com/logout?${params.toString()}`;
}
